import {Tooltip} from '@mui/material';
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

import {eventDemographics as demographicCategories, NILL} from "@lib/constants";
import moment from '@lib/moment';
import { isEmpty } from '@lib/utils';

export const getEventStatus = (event) => {
  if (event.status === 'COMPLETED' || event.status === 'CANCELED') {
    return event.status;
  }
  return 'INCOMPLETE';
}

export const getEventStatusIcon = (event) => getStatusIcon(getEventStatus(event));

export const getStatusOrd = (event) => {
  switch (getEventStatus(event)) {
    case 'COMPLETED':
      return 2;
    case 'CANCELED':
      return 0;
    case 'INCOMPLETE':
    default:
      return 1;
  }
};

export const getEventDates = (event) => {
  if (!event.sessionSummary?.first || !event.sessionSummary?.last) {
    return NILL;
  }
  const start = moment(event.sessionSummary.first).format('M/D/yyyy');
  const end = moment(event.sessionSummary.last).format('M/D/yyyy');
  if (start === end) {
    return start;
  } else {
    return `${start} - ${end}`;
  }
}

/**
 * Convert a lesson into an Autocomplete dropdown option object.
 *
 * @param lesson
 * @returns {{id, label: *, audience}|{id: null, label: string, audience: string}}
 */
export const toLessonOption = (lesson) => {
  if (!lesson) {
    return {
      id: null,
      label: '',
      audience: '',
    };
  }
  return {
    id: lesson.id,
    label: lesson.curriculum_name || lesson.booth_topic || lesson.lesson_name,
    audience: lesson.audience,
  };
};

export const getStatusIcon = (status) => {
  switch (status) {
    case 'COMPLETED':
      return (
        <Tooltip title="Completed">
          <CheckCircleIcon color="success" />
        </Tooltip>
      );
    case 'CANCELED':
      return (
        <Tooltip title="Canceled">
          <CancelRoundedIcon color="error" />
        </Tooltip>
      );
    default:
      return (
        <Tooltip title="Incomplete">
          <ErrorIcon color="yellow" />
        </Tooltip>
      );
  }
}

/**
 * Whether we should collect demographic data for an event. Determines if the event is a type for which we
 * collect demographics. Does not check whether demographics data have been entered for the event.
 */
export const shouldCollectDemographics = (event) => {
  return event.event_type === 'CLASS';
}

export const getMaxSessionParticipants = (event) => {
  return event.sessions?.reduce((acc, { completed }) => Math.max(acc, completed?.participant_count || 0), 0) || 0;
}

export const getDemographicsSummary = (event, values = null) => {
  let demo = values || {};
  if (!values) {
    demo = parseDemographics(event);
  }
  const participants = getMaxSessionParticipants(event);
  const sums = {
    age: demo.age ? Object.values(demo.age).reduce((acc, val) => acc + (val || 0), 0) : 0,
    gender: demo.gender ? Object.values(demo.gender).reduce((acc, val) => acc + (val || 0), 0) : 0,
    race: demo.race ? Object.values(demo.race).reduce((acc, val) => acc + (val || 0), 0) : 0,
  }
  const groupsValid = {
    age: sums.age === participants,
    gender: sums.gender === participants,
    race: sums.race >= participants,
  }
  return {
    valid: Object.values(groupsValid).every(v => v),
    participants,
    sums,
    groupsValid
  };
};

export const maybeParseJson = (value) => {
  if (!value) {
    return {};
  }
  if (typeof value === 'string') {
    return JSON.parse(value);
  }
  return value;
}

const parseDemographics = (event) => {
  return {
    age: event.demographics?.age ? maybeParseJson(event.demographics.age) : {},
    gender: event.demographics?.gender ? maybeParseJson(event.demographics.gender) : {},
    race: event.demographics?.raceEthnicity ? maybeParseJson(event.demographics.raceEthnicity) : {},
  }
}

// Parse demographics data from the event into values we can use in the form.
export const getDemographicsInputValues = (event) => {
  if (!event) {
    return {};
  }
  // JSON fields are stored as maps in Dynamo, but are sent as JSON-stringified text
  // from the API, so we need to parse them.
  const demographics = parseDemographics(event);

  const defaultValues = {
    age: {},
    gender: {},
    race: {},
    source: event.demographics?.source,
    comments: event.comments,
  };
  for (const [key, value] of Object.entries(demographics.age)) {
    defaultValues.age[key] = isEmpty(value) ? null : parseInt(value);
  }
  for (const [key, value] of Object.entries(demographics.gender)) {
    defaultValues.gender[key] = isEmpty(value) ? null : parseInt(value);
  }
  for (const [key, value] of Object.entries(demographics.race)) {
    defaultValues.race[key] = isEmpty(value) ? null : parseInt(value);
  }
  return defaultValues;
}

// Gets an array of the relevant age groups for an event, based on curriculum
export const getAgeGroups = (event) => {
  return demographicCategories.ageRange.filter(a => {
    if (a.alwaysShown) {
      return true;
    }
    if (event.curriculum?.age_ranges?.includes(a.id)) {
      // Adult curriculum: 9th-12th age range only appears if teen_parents is true
      if (a.id === '9-12' && event.curriculum?.audience === 'Adults' && !event.teen_parents) {
        return false;
      }
      return true;
    }
    return false;
  });
}

// Checks if an event has complete demographic information
export const hasCompleteDemographics = (event) => {
  if (!event) {
    return false;
  }

  // JSON fields are stored as maps in Dynamo, but are sent as JSON-stringified text
  // from the API, so we need to parse them.
  const demographics = parseDemographics(event);

  const ageRanges = getAgeGroups(event);
  for (const {id} of ageRanges) {
    if (isEmpty(demographics.age[id])) {
      return false;
    }
  }
  for (const {id} of demographicCategories.gender) {
    if (isEmpty(demographics.gender[id])) {
      return false;
    }
  }
  for (const {id} of demographicCategories.raceEthnicity) {
    if (isEmpty(demographics.race[id])) {
      return false;
    }
  }
  return true;
}
